import React, { useCallback, useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Legend,
} from 'recharts';
import { Loader2, RefreshCw, AlertCircle } from 'lucide-react';
import axios from 'axios';
import {
  avgLegendForDashboardRange,
  DashboardChartRange,
  labelForDashboardRange,
} from '../lib/dashboardChartRange';

export interface SmtpComMonthStat {
  month: string;
  label: string;
  sent: number;
  delivered: number;
  bounced: number;
  bounceRate: number | null;
  deliveredRate: number | null;
  partial: boolean;
}

interface SmtpComMonthlyStatsResult {
  configured: boolean;
  months: SmtpComMonthStat[];
  averageBounceRate: number | null;
  cachedAt: string | null;
  chartMonths?: number;
  range?: DashboardChartRange;
  rangeLabel?: string;
  granularity?: 'month' | 'day' | 'hour';
  fromCache?: boolean;
  error?: string;
}

function pct2(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `${n.toFixed(2)}%`;
}

function CustomTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload as SmtpComMonthStat & { avgLine?: number | null };
  if (!row) return null;
  return (
    <div className="rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 shadow-lg text-xs space-y-1 min-w-[160px]">
      <div className="font-semibold text-gray-900 dark:text-white">
        {row.label}
        {row.partial ? <span className="ml-1 font-normal text-amber-600">(partial)</span> : null}
      </div>
      <div className="text-gray-600 dark:text-slate-300">
        Bounce rate: <span className="font-medium tabular-nums">{pct2(row.bounceRate)}</span>
      </div>
      <div className="text-gray-600 dark:text-slate-300">
        Delivered: <span className="font-medium tabular-nums">{pct2(row.deliveredRate)}</span>
      </div>
      <div className="text-gray-600 dark:text-slate-300">
        Sent: <span className="font-medium tabular-nums">{row.sent.toLocaleString()}</span>
      </div>
    </div>
  );
}

interface Props {
  range: DashboardChartRange;
}

export default function SmtpComBounceChart({ range }: Props) {
  const [data, setData] = useState<SmtpComMonthlyStatsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (refresh = false, signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get<SmtpComMonthlyStatsResult>('/api/smtpcom/monthly-stats', {
          params: {
            range,
            ...(refresh ? { refresh: '1' } : {}),
          },
          signal,
        });
        if (signal?.aborted) return;
        setData(res.data);
        if (res.data.error && res.data.configured) {
          setError(res.data.error);
        }
      } catch (e: any) {
        if (axios.isCancel?.(e) || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || signal?.aborted) {
          return;
        }
        setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load SMTP.com stats');
        // Do not clear prior series — aborted/stale range switches were blanking the chart.
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [range]
  );

  useEffect(() => {
    const ac = new AbortController();
    void load(false, ac.signal);
    return () => ac.abort();
  }, [load]);

  const chartRows = (data?.months ?? []).map((m) => ({
    ...m,
    bounceRate: m.sent > 0 ? m.bounceRate : null,
    avgLine: data?.averageBounceRate ?? null,
  }));

  const rangeLabel = data?.rangeLabel ?? labelForDashboardRange(range);
  const avgLegend = avgLegendForDashboardRange(range);
  const showInitialSpinner = loading && !data;
  const showFatalError = !loading && error && !(data?.months?.length);

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white min-w-0">
          SMTP.com Bounce Rate
          {data && (
            <span className="font-semibold">
              {' | '}
              {rangeLabel} average{' '}
              {data.averageBounceRate != null ? pct2(data.averageBounceRate) : '—'}
              {data.cachedAt && (
                <>
                  {' - synced '}
                  {new Date(data.cachedAt).toLocaleDateString('en-US')}
                </>
              )}
            </span>
          )}
        </h2>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50"
          title="Force refresh from SMTP.com"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {showInitialSpinner ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
        </div>
      ) : showFatalError ? (
        <div className="px-4 py-10 text-center space-y-2">
          <AlertCircle className="h-6 w-6 text-amber-500 mx-auto" />
          <p className="text-sm text-gray-600 dark:text-slate-300">{error}</p>
          {!data?.configured && (
            <p className="text-xs text-gray-400 dark:text-slate-500">
              Admin → Email / SMTP → enable SMTP.com API and save your key.
            </p>
          )}
        </div>
      ) : (
        <div className="p-4 relative">
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-slate-800/50 rounded-b-lg">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
            </div>
          )}
          {error && (
            <div className="mb-3 flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
              {error}
            </div>
          )}
          {data && !data.months.some((m) => m.sent > 0) && !error && (
            <div className="mb-3 flex items-center gap-2 text-xs text-gray-600 dark:text-slate-300 bg-gray-50 dark:bg-slate-700/40 border border-gray-200 dark:border-slate-600 rounded-md px-3 py-2">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
              No send volume in the SMTP.com stats response for this window — axes show the range, but there is nothing to plot yet.
            </div>
          )}
          <div className="h-72 w-full min-w-0">
            <ResponsiveContainer key={range} width="100%" height="100%" minWidth={0}>
              <LineChart data={chartRows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#37415133" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10 }}
                  interval="preserveStartEnd"
                  minTickGap={16}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `${Number(v).toFixed(1)}%`}
                  width={48}
                  domain={[0, (dataMax: number) => Math.max(2, Math.ceil(dataMax * 1.15 * 10) / 10)]}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: 12 }}
                  formatter={(value) =>
                    value === 'bounceRate' ? 'Bounce rate' : value === 'avgLine' ? avgLegend : value
                  }
                />
                {data?.averageBounceRate != null && (
                  <ReferenceLine
                    y={data.averageBounceRate}
                    stroke="#94a3b8"
                    strokeDasharray="4 4"
                    strokeWidth={1.5}
                  />
                )}
                <Line
                  type="monotone"
                  dataKey="bounceRate"
                  name="bounceRate"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={{ r: 2.5, strokeWidth: 0, fill: '#ef4444' }}
                  activeDot={{ r: 4 }}
                  connectNulls={false}
                />
                <Line
                  type="monotone"
                  dataKey="avgLine"
                  name="avgLine"
                  stroke="#94a3b8"
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  dot={false}
                  activeDot={false}
                  legendType="plainline"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
